import { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { sos as sosApi } from '@/lib/trackizy';
import useOrg from '@/hooks/useOrg';
import useFeatures from '@/hooks/useFeatures';
import { applySosEvent } from '@/features/safety/safetyModel';

export default function useSOS() {
  const org = useOrg();
  const user = useSelector((state) => state.session.user);
  const { guardian } = useFeatures();
  const [activeAlerts, setActiveAlerts] = useState([]);
  const [loading, setLoading] = useState(false);

  const pollRef = useRef(null);

  // ─── Fetch active SOS alerts + poll every 10s ───────────────
  useEffect(() => {
    // Guardian gates the whole SOS surface server-side (orgFlagGuard on
    // /api/safety/*) — without this check a non-entitled org would still
    // mount this hook (it's unconditional in AppLayout) and poll into a
    // guaranteed 403 every 60s, console.error-ing with nothing on screen
    // to explain it. See src/CLAUDE.md's two-level gating rule.
    if (!org.id || !guardian) return;

    const fetchAlerts = async () => {
      setLoading(true);
      try {
        const data = await sosApi.list();
        setActiveAlerts(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to fetch SOS alerts:', err);
      }
      setLoading(false);
    };

    fetchAlerts();

    // Long-tail safety-net refresh — push updates arrive via WebSocket below.
    pollRef.current = setInterval(fetchAlerts, 60_000);

    // Subscribe to pushed SOS events
    const onWs = (e) => {
      try {
        const data = JSON.parse(e.data);
        setActiveAlerts((prev) => applySosEvent(prev, data));
      } catch {
        // ignore
      }
    };
    window.addEventListener('ws-message', onWs);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      window.removeEventListener('ws-message', onWs);
    };
  }, [org.id, guardian]);

  // ─── Acknowledge SOS ─────────────────────────────────────────
  const acknowledge = useCallback(
    async (alertId) => {
      if (!user) return;

      try {
        await sosApi.acknowledge(alertId);
        setActiveAlerts((prev) => prev.filter((a) => a.id !== alertId));
      } catch (err) {
        console.error('Failed to acknowledge SOS:', err);
      }
    },
    [user]
  );

  return {
    activeAlerts,
    loading,
    acknowledge,
    activeCount: activeAlerts.length,
  };
}
